import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import useSelectedFilterLabels from '../../../hooks/useSelectedFilterLabels';
import { useAccountsFiltersQuery } from '../useAccountsFiltersQuery';

interface UseTeamFiltersProps {
  value: string[];
  onSubmit: (data: SearchableFormValues) => void;
  oemSegmentsIds?: string[];
  sentinelTeamsIds?: string[];
}

export default function useAccountsFilters({
  value,
  oemSegmentsIds = [],
  sentinelTeamsIds = [],
  onSubmit,
}: UseTeamFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: accountsData,
    loading,
    loadMore,
    loadingMore,
  } = useAccountsFiltersQuery({
    variables: {
      page: 1,
      text: debouncedSearchValue,
      oemSegmentsIds,
      sentinelTeamsIds,
    },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      (accountsData?.managedPlatformAccounts.collection ?? []).map(
        (record) => ({
          id: record.id,
          label: record.name,
          value: record.id,
        }),
      ),
    [accountsData],
  );

  const labelsById = useSelectedFilterLabels(items);

  // When a single account is pre-selected but is not part of the currently
  // loaded results (e.g. it lives on a later page), fetch it by id so the
  // filter button can show its name instead of the generic fallback.
  const selectedId = value.length === 1 ? value[0] : undefined;

  const { data: selectedData } = useAccountsFiltersQuery({
    variables: {
      page: 1,
      text: '',
      ids: selectedId ? [selectedId] : [],
      oemSegmentsIds: [],
      sentinelTeamsIds: [],
    },
    skip:
      !selectedId ||
      !accountsData ||
      items.some((item) => item.value === selectedId) ||
      Boolean(labelsById[selectedId]),
  });

  const selectedLabel = selectedData?.managedPlatformAccounts.collection.find(
    (record) => record.id === selectedId,
  )?.name;

  const text = useMemo(() => {
    if (value.length === 0) return t`All Accounts`;

    if (value.length === 1)
      return (
        labelsById[value[0]] ||
        items.find((item) => item.value === value[0])?.label ||
        selectedLabel ||
        t`Account`
      );

    return t`Accounts (${value.length})`;
  }, [labelsById, items, selectedLabel, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
